// Firebase Functions Service
// Handles calls to Firebase Cloud Functions for DVLA and MOT lookups

import { auth } from '../firebase/config';
import { firebaseConfig as appFirebaseConfig, apiConfig } from '../config/app';

const getAuthHeaders = async () => {
  const user = auth.currentUser;
  if (!user) {
    return {};
  }
  const token = await user.getIdToken();
  return { Authorization: `Bearer ${token}` };
};

/**
 * Get the Firebase Functions URL for a given function name
 * Uses app config so Gen 2 (Cloud Run) URLs can be set via env per project
 */
const getFunctionUrl = (functionName) => appFirebaseConfig.getFunctionUrl(functionName);

/**
 * DVLA Vehicle Lookup
 * @param {string} registrationNumber - Vehicle registration number
 * @returns {Promise<Object>} Vehicle data from DVLA
 */
export const dvlaLookup = async (registrationNumber) => {
  if (!registrationNumber || !registrationNumber.trim()) {
    throw new Error('Registration number is required');
  }

  const reg = registrationNumber.trim().toUpperCase().replace(/\s+/g, '');

  try {
    // Create abort controller for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), apiConfig.timeout.default);

    const functionUrl = getFunctionUrl('dvlaLookup');
    
    console.log('🔍 DVLA Lookup - Using Firebase Function:', functionUrl);
    console.log('🔍 DVLA Lookup - Registration:', reg);
    
    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(await getAuthHeaders()),
      },
      body: JSON.stringify({ registrationNumber: reg }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));

      if (response.status === 401 || response.status === 403) {
        throw new Error('DVLA API authentication failed. Please check server configuration.');
      }

      if (response.status === 404) {
        throw new Error(errorData.error || 'Registration not found in DVLA database');
      }

      if (response.status === 429) {
        throw new Error('Too many requests. Please wait a moment and try again.');
      }

      throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    // Validate that we got actual vehicle data
    if (!data || (typeof data === 'object' && Object.keys(data).length === 0)) {
      throw new Error('No vehicle data found for this registration');
    }

    console.log('✅ DVLA Lookup - Success');
    return data;
  } catch (error) {
    console.error('❌ DVLA Lookup Error:', error);
    console.error('❌ Error name:', error.name);
    console.error('❌ Error message:', error.message);
    
    if (error.name === 'AbortError') {
      throw new Error('DVLA lookup timed out. Please try again.');
    }
    if (error.name === 'TypeError' && (error.message.includes('fetch') || error.message.includes('Failed to fetch'))) {
      throw new Error('Cannot connect to DVLA service. Please check that the Firebase function is deployed and has public access permissions.');
    }
    throw error;
  }
};

/**
 * MOT website lookup (make + model via freemotcheck.uk proxy)
 * Soft-fails: returns null when unavailable so callers can fall back to DVLA make/year.
 * @param {string} registrationNumber - Vehicle registration number
 * @returns {Promise<{ make: string, model: string }|null>}
 */
export const motWebsiteLookup = async (registrationNumber) => {
  if (!registrationNumber || !registrationNumber.trim()) {
    return null;
  }

  const reg = registrationNumber.trim().toUpperCase().replace(/\s+/g, '');

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), Math.min(apiConfig.timeout.default, 10000));

    const functionUrl = getFunctionUrl('motWebsiteLookup');

    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(await getAuthHeaders()),
      },
      body: JSON.stringify({ registrationNumber: reg }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      console.warn('⚠️ MOT website lookup unavailable:', response.status);
      return null;
    }

    const data = await response.json();
    const make = data?.make ? String(data.make).trim() : '';
    const model = data?.model ? String(data.model).trim() : '';
    if (!make && !model) return null;

    console.log('✅ MOT website lookup - Success:', make, model);
    return { make, model };
  } catch (error) {
    if (error.name === 'AbortError') {
      console.warn('⚠️ MOT website lookup timed out');
    } else {
      console.warn('⚠️ MOT website lookup failed:', error.message);
    }
    return null;
  }
};

/**
 * MOT Check
 * @param {string} registrationNumber - Vehicle registration number
 * @returns {Promise<Object>} MOT data from MOT API
 */
export const motCheck = async (registrationNumber) => {
  if (!registrationNumber || !registrationNumber.trim()) {
    throw new Error('Registration number is required');
  }

  const reg = registrationNumber.trim().toUpperCase().replace(/\s+/g, '');

  try {
    // Create abort controller for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), apiConfig.timeout.default);

    const functionUrl = getFunctionUrl('motCheck');

    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(await getAuthHeaders()),
      },
      body: JSON.stringify({ registrationNumber: reg }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));

      if (response.status === 503) {
        // MOT API not configured - this is expected, not an error
        throw new Error('MOT_API_NOT_CONFIGURED');
      }

      if (response.status === 404) {
        throw new Error(errorData.error || 'MOT data not found for this registration');
      }

      if (response.status === 401 || response.status === 403) {
        throw new Error('MOT API authentication failed. Please check server configuration.');
      }

      throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    // Handle array response (MOT API returns array)
    if (Array.isArray(data)) {
      if (data.length === 0) {
        throw new Error('No MOT data found for this registration');
      }
      // Return first vehicle if array
      return data[0];
    }

    return data;
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('MOT lookup timed out. Please try again.');
    }
    if (error.name === 'TypeError' && (error.message.includes('fetch') || error.message.includes('Failed to fetch'))) {
      throw new Error('Cannot connect to MOT service. Please check your internet connection.');
    }
    throw error;
  }
};

/**
 * List All Firebase Auth Users
 * @returns {Promise<Object>} List of all Firebase Auth users
 */
export const listAuthUsers = async () => {
  try {
    const functionUrl = getFunctionUrl('listAuthUsers');
    const projectId = appFirebaseConfig.projectId || 'unknown';
    console.log('🔍 Calling listAuthUsers function:');
    console.log('   URL:', functionUrl);
    console.log('   Project ID:', projectId);
    
    // Create abort controller for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), apiConfig.timeout.default);
    
    const response = await fetch(functionUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(await getAuthHeaders()),
      },
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('listAuthUsers error response:', response.status, errorData);
      
      if (response.status === 403) {
        const projectId = appFirebaseConfig.projectId || 'your-project-id';
        const region = appFirebaseConfig.region || 'us-central1';
        throw new Error(`Permission denied (403). The listAuthUsers function needs public access. For Gen 2 functions run: gcloud run services add-iam-policy-binding listAuthUsers --region=${region} --member="allUsers" --role="roles/run.invoker" --project=${projectId}. Or run: ./scripts/set-functions-iam.sh ${projectId} ${region}`);
      }
      
      if (response.status === 404) {
        throw new Error('Cloud Function not found. Please deploy the function first by running: firebase deploy --only functions:listAuthUsers');
      }
      
      if (response.status === 500) {
        throw new Error(errorData.message || 'Server error while listing users. Check Cloud Function logs.');
      }
      
      throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log('listAuthUsers success:', data);
    return data;
  } catch (error) {
    console.error('Error listing Firebase Auth users:', error);
    
    if (error.name === 'AbortError') {
      throw new Error('Request timed out. Please try again.');
    }
    
    if (error.name === 'TypeError' && (error.message.includes('fetch') || error.message.includes('Failed to fetch'))) {
      throw new Error('Cannot connect to Cloud Function. The function may not be deployed. Please run: firebase deploy --only functions:listAuthUsers');
    }
    
    // Re-throw with more context if it's our custom error
    if (error.message.includes('Cloud Function not found') || error.message.includes('Cannot connect')) {
      throw error;
    }
    
    throw new Error(error.message || 'Failed to load Firebase Auth users. Please try again.');
  }
};

/**
 * Send Password Reset Email
 * Sends a password reset email to a Firebase Auth user
 * @param {string} email - User's email address
 * @param {string} uid - Optional: User's Firebase Auth UID (alternative to email)
 * @returns {Promise<Object>} Success response
 */
export const sendPasswordReset = async (email, uid = null) => {
  if (!email && !uid) {
    throw new Error('Email or UID is required');
  }

  try {
    const functionUrl = getFunctionUrl('sendPasswordReset');
    console.log('Calling sendPasswordReset function at:', functionUrl);
    
    // Create abort controller for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), apiConfig.timeout.default);
    
    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(await getAuthHeaders()),
      },
      body: JSON.stringify({ email, uid }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('sendPasswordReset error response:', response.status, errorData);
      
      if (response.status === 404) {
        throw new Error('Cloud Function not found. Please deploy the function first by running: firebase deploy --only functions:sendPasswordReset');
      }
      
      if (response.status === 400) {
        throw new Error(errorData.error || 'Invalid request. Email or UID is required.');
      }
      
      if (response.status === 500) {
        throw new Error(errorData.message || 'Server error while sending password reset. Check Cloud Function logs.');
      }
      
      throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log('sendPasswordReset success:', data);
    return data;
  } catch (error) {
    console.error('Error sending password reset:', error);
    
    if (error.name === 'AbortError') {
      throw new Error('Request timed out. Please try again.');
    }
    
    if (error.name === 'TypeError' && (error.message.includes('fetch') || error.message.includes('Failed to fetch'))) {
      throw new Error('Cannot connect to Cloud Function. The function may not be deployed. Please run: firebase deploy --only functions:sendPasswordReset');
    }
    
    // Re-throw with more context if it's our custom error
    if (error.message.includes('Cloud Function not found') || error.message.includes('Cannot connect')) {
      throw error;
    }
    
    throw new Error(error.message || 'Failed to send password reset email. Please try again.');
  }
};

/**
 * Send vehicle images/videos as email attachments.
 * @param {Object} payload
 * @param {string} payload.to - Recipient email
 * @param {string} payload.subject - Email subject
 * @param {string} payload.vehicleReg - Vehicle registration
 * @param {string} payload.vehicleName - Vehicle name/description
 * @param {string[]} payload.images - Image URLs
 * @param {string[]} payload.videosBefore - Before video URLs
 * @param {string[]} payload.videosAfter - After video URLs
 * @returns {Promise<Object>}
 */
export const sendVehicleMediaEmail = async (payload) => {
  try {
    const functionUrl = getFunctionUrl('sendVehicleMediaEmail');
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), apiConfig.timeout.default * 3);

    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(await getAuthHeaders()),
      },
      body: JSON.stringify(payload),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Sending media email timed out. Please try with fewer files.');
    }
    if (error.name === 'TypeError' && (error.message.includes('fetch') || error.message.includes('Failed to fetch'))) {
      throw new Error('Cannot connect to Cloud Function. Please deploy functions and try again.');
    }
    throw new Error(error.message || 'Failed to send vehicle media email.');
  }
};

/**
 * Send quote/estimate to customer via email.
 * Used by manager in Technician Dispatch approval flow.
 * @param {Object} payload - to, companyName, vehicleReg, vehicleName, customerName, estimateAmount, estimateDescription, summary
 * @returns {Promise<Object>}
 */
export const sendQuoteToCustomer = async (payload) => {
  const functionUrl = getFunctionUrl('sendQuoteToCustomer');
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), apiConfig.timeout.default);

  try {
    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(await getAuthHeaders()),
      },
      body: JSON.stringify(payload),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    const text = await response.text();
    let errorData = {};
    try {
      errorData = text ? JSON.parse(text) : {};
    } catch (_) {
      errorData = { message: text || `HTTP ${response.status}` };
    }

    if (!response.ok) {
      const msg = errorData.error || errorData.message || `HTTP error! status: ${response.status}`;
      if (response.status === 404) {
        throw new Error('Quote email function not found. Deploy with: firebase deploy --only functions:sendQuoteToCustomer');
      }
      if (response.status === 401) {
        throw new Error('Please sign in to send the quote.');
      }
      if (response.status === 500 && errorData.message?.includes('SMTP')) {
        throw new Error('Email not configured. Contact support to set up SMTP.');
      }
      throw new Error(msg);
    }

    return text ? JSON.parse(text) : {};
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Sending quote email timed out. Please try again.');
    }
    if (error.name === 'TypeError' && (error.message.includes('fetch') || error.message.includes('Failed to fetch'))) {
      throw new Error('Cannot connect to email service. Ensure sendQuoteToCustomer is deployed and reachable.');
    }
    throw error;
  }
};

// Export Stripe functions (imported from stripeService)
export { createCheckoutSession, createPortalSession, getSubscriptionStatus } from './stripeService';

const firebaseFunctionsApi = {
  dvlaLookup,
  motWebsiteLookup,
  motCheck,
  listAuthUsers,
  sendPasswordReset,
  sendVehicleMediaEmail,
  createUser: async (userData) => {
    const functionUrl = getFunctionUrl('createUser');
    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(await getAuthHeaders()),
      },
      body: JSON.stringify(userData),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorData.error || 'Failed to create user');
    }
    return response.json();
  },
  updateUser: async (uid, updates) => {
    const functionUrl = getFunctionUrl('updateUser');
    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(await getAuthHeaders()),
      },
      body: JSON.stringify({ uid, updates }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorData.error || 'Failed to update user');
    }
    return response.json();
  },
  deleteUser: async (uid) => {
    const functionUrl = getFunctionUrl('deleteUser');
    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(await getAuthHeaders()),
      },
      body: JSON.stringify({ uid }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorData.error || 'Failed to delete user');
    }
    return response.json();
  },
};

export default firebaseFunctionsApi;

